<template>
  <section class="mega-vertical-spacing">
    <div>
      <div class="md-vertical-spacing dashboard-job-module">
        <div v-for="job in eligibleJobs" :key="job.id">
          <SeekerJobCard :job="job" :account="account" :class="jobCardClass" @click="handleJobClick" @manage-job="onManageJob">
            <div v-if="showShareJob" class="dashboard-job-module__divider"></div>
            <div v-if="showShareJob" class="dashboard-job-module__share-job-card">
              <div class="dashboard-job-module__row-item">
                <ProgressIcon class="dashboard-job-module__progress-icon min-w-16" color="#FF0064" :width="16" :height="16" />
                <div class="dashboard-job-module__col-item">
                  <h3 class="dashboard-job-module__content-slim xs-vertical-spacing-top body-1">
                    We're sharing your job with sitters
                  </h3>
                  <p class="text-small text-grey-clickable xs-vertical-spacing dashboard-job-module__content-slim">
                    {{ shareJobContent }}
                  </p>
                  <Button
                    v-if="accountType === 'basic'"
                    variant="primary"
                    href="/upgrade/pricing"
                    value="Upgrade now"
                    class="self-start mb-16"
                  />
                </div>
              </div>
            </div>
          </SeekerJobCard>
        </div>
      </div>
      <Button v-if="jobs.length > 0" class="p-0" variant="text" href="/seeker/my-jobs/opened" value="All jobs" />
    </div>
    <ActionsPopup v-model="showManagePopup" title="Manage Job" :buttons="manageJobButtons">
      <p v-if="currentJob?.title" class="text-body">{{ currentJob?.title }}</p>
    </ActionsPopup>
    <ActionsPopup v-model="showCloseConfirmation" title="Close this job?" :buttons="closeConfirmationButtons">
      <p class="text-body">Once you close this job, new sitters will no longer be able to apply for it</p>
    </ActionsPopup>
  </section>
</template>

<script lang="ts">
import ActionsPopup from '@/components/actionsPopup/ActionsPopup.vue';
import type { ActionButton } from '@/components/actionsPopup/types';
import SeekerJobCard from '@/components/seekerJobCard/SeekerJobCard.vue';
import Button from '@/components/button/Button.vue';
import ProgressIcon from '@/graphics/icons/ProgressIcon.vue';
import { redirect } from '@/helpers/redirects';
import {
  createViewApplicantsButton,
  createReopenJobButton,
  createCloseConfirmationButtons,
  createActiveJobManagementButtons,
} from '@/helpers/jobActionButtons';
import { getSeasonalMessage } from './helpers/seasonalHelper';
import { Account } from '@/lib/public_api/Account';

export default {
  name: 'JobsModule',
  components: {
    ActionsPopup,
    SeekerJobCard,
    Button,
    ProgressIcon,
  },
  props: {
    jobs: {
      type: Array,
      required: true,
    },
    eligibleJobs: {
      type: Array,
      required: true,
    },
    account: {
      required: true,
      type: Account,
    },
  },
  data() {
    return {
      currentJob: null as any,
      showManagePopup: false,
      showCloseConfirmation: false,
    };
  },
  computed: {
    closeConfirmationButtons(): ActionButton[] {
      return createCloseConfirmationButtons({
        onConfirm: () => this.closeJobMutation(),
        onCancel: () => this.cancelCloseConfirmation(),
      });
    },
    manageJobButtons(): ActionButton[] {
      const job = this.currentJob;
      if (!job) return [];
      const edges = job.applications?.edges ?? [];
      const totalOpenApplicants = edges.filter((app: any) => app.node.status === 'open');
      const hasApplicants = totalOpenApplicants.length > 0;
      const isClosed = job.status === 'JOB_STATUS_CLOSED';
      const currentUserOwnsJob = this.account.id === job.parent.id;
      const isActiveOrPending = job.isActive || job.status === 'PENDING';

      let buttons: ActionButton[] = [];
      if (hasApplicants && (job.isActive || job.status === 'JOB_STATUS_CLOSED')) {
        buttons.push(
          createViewApplicantsButton(job.id, {
            variant: job.isActive ? 'primary' : 'outlined',
          })
        );
      }
      // the list of jobs returned for an account includes jobs that have been shared with the account
      // this checks that the user is the manager of the job
      if (currentUserOwnsJob) {
        if (isActiveOrPending) {
          buttons = buttons.concat(
            createActiveJobManagementButtons({
              jobId: job.id,
              onCloseJob: () => this.openCloseConfirmation(),
              isFamilyConcierge: this.account.isFamilyConcierge(),
            })
          );
        }

        if (!isActiveOrPending || isClosed) {
          buttons.push(createReopenJobButton(job.id));
        }
      }
      return buttons;
    },
    showShareJob(): boolean {
      if (this.eligibleJobs.length !== 1) return false;

      const j = this.eligibleJobs[0] as any;
      if (['PENDING', 'JOB_STATUS_PENDING'].includes(j.status) || j.applications.totalCount > 0) return false;

      return true;
    },
    jobCardClass(): Record<string, boolean> {
      return { 'mb-16': !this.oneJobAndNoApps };
    },
    shareJobContent(): string {
      return this.premOrCorp ? 'You can also search for caregivers you like and reach out directly.' : getSeasonalMessage();
    },
    premOrCorp(): boolean {
      const accType = this.accountType;
      return accType === 'premium' || accType === 'corporate';
    },
    oneJobAndNoApps(): boolean {
      const oneJob = (this as any).eligibleJobs.length === 1;
      const noApps = (this.eligibleJobs[0] as any).applications.totalCount === 0;
      return oneJob && noApps;
    },
    accountType(): string | null {
      return this.account.accountType();
    },
  },
  methods: {
    onManageJob(job: any) {
      this.currentJob = job;
      this.showManagePopup = true;
    },
    openCloseConfirmation() {
      this.showManagePopup = false;
      this.showCloseConfirmation = true;
    },
    cancelCloseConfirmation() {
      this.showCloseConfirmation = false;
      this.showManagePopup = true;
    },
    async closeJobMutation() {
      if (!this.currentJob) return;

      const jobId = this.currentJob.id;
      this.showCloseConfirmation = false;

      try {
        await this.$store.dispatch('job/closeJob', jobId);
        redirect(`/jobs/action_with_uuid?job_action=job_closed_questionnaire&uuid=${jobId}`);
      } catch {
        // Error already logged in store action
      }
    },
    handleJobClick(card: any) {
      redirect(card.jobLink);
    },
  },
};
</script>
